import { useMemo, useState } from "react"
import { useQuery } from "react-query"
import { useTranslation } from "react-i18next"
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid"
import { fetchOrderStockAllocation } from "../../services"
import { SkeletonLoader } from "../../components/SkeletonLoader"
import { BetaBadge } from "../../components/BetaBadge"
import { PICK_TYPE_STYLES } from "../../utils/pickingStyles"

const STATUS_BADGE_STYLES = {
    SEALED: "bg-purple-100 text-purple-800",
    ACTIVE: "bg-blue-100 text-blue-800",
    RESERVED: "bg-yellow-100 text-yellow-800",
    PICKED: "bg-green-100 text-green-800",
    in_use: "bg-blue-100 text-blue-800"
}

const Badge = ({ value, styles }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles || "bg-gray-100 text-gray-800"}`}>
        {value}
    </span>
)

// Content signatures used to decide whether two entities can collapse into a
// single "×N" row. Pieces compare by (product, lot, status); boxes also factor
// in their pieces so two boxes with the same count but different SKUs do not
// merge; pallets propagate box signatures so the same applies one level up.
const pieceSig = (p) => `${p.product_id || ""}|${p.sku || ""}|${p.lot_code || ""}|${p.status || ""}`
const boxSig = (b) => {
    const pieces = (b.pieces || []).map(pieceSig).sort().join(",")
    return `${b.status || ""}|${(b.pieces || []).length}|${pieces}`
}

const groupPieces = (pieces) => {
    if (!Array.isArray(pieces)) return []
    const map = new Map()
    for (const p of pieces) {
        const key = pieceSig(p)
        if (!map.has(key)) {
            map.set(key, { ...p, count: 1 })
        } else {
            map.get(key).count += 1
        }
    }
    return Array.from(map.values())
}

const groupBoxes = (boxes) => {
    if (!Array.isArray(boxes)) return []
    const map = new Map()
    for (const box of boxes) {
        const key = `${box.location?.id || ""}|${boxSig(box)}`
        if (!map.has(key)) {
            map.set(key, { ...box, count: 1, sample_barcode: box.barcode })
        } else {
            map.get(key).count += 1
        }
    }
    return Array.from(map.values())
}

const groupPallets = (pallets) => {
    if (!Array.isArray(pallets)) return []
    const map = new Map()
    for (const pallet of pallets) {
        const boxesKey = (pallet.boxes || []).map(boxSig).sort().join("::")
        const key = `${pallet.status || ""}|${pallet.location?.id || ""}|${(pallet.boxes || []).length}|${boxesKey}`
        if (!map.has(key)) {
            map.set(key, { ...pallet, count: 1, sample_barcode: pallet.barcode })
        } else {
            map.get(key).count += 1
        }
    }
    return Array.from(map.values())
}

const PieceRow = ({ piece, i18n }) => (
    <div className="flex items-center justify-between text-xs py-1 px-2 bg-gray-50 rounded">
        <div className="flex items-center gap-2">
            <span className="font-mono text-gray-700">{piece.sku || `#${piece.stock_unit_id}`}</span>
            {piece.lot_code && <span className="text-gray-500">{i18n.t('stock_allocation.lot')}: {piece.lot_code}</span>}
            {piece.status && <Badge value={i18n.t(`stock_allocation.statuses.${piece.status}`, { defaultValue: piece.status })} styles={STATUS_BADGE_STYLES[piece.status]} />}
        </div>
        <span className="text-gray-600 font-medium">×{piece.count}</span>
    </div>
)

const PieceList = ({ pieces, i18n }) => {
    const grouped = useMemo(() => groupPieces(pieces), [pieces])
    if (grouped.length === 0) return null
    return (
        <div className="flex flex-col gap-1 mt-2">
            {grouped.map((p, idx) => <PieceRow key={idx} piece={p} i18n={i18n} />)}
        </div>
    )
}

const BoxBlock = ({ box, count, i18n }) => {
    const [expanded, setExpanded] = useState(false)
    const piecesCount = (box.pieces || []).length
    const Chevron = expanded ? ChevronDownIcon : ChevronRightIcon
    return (
        <div className="border border-gray-200 rounded p-2">
            <button type="button" onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between text-left">
                <div className="flex items-center gap-2">
                    <Chevron className="h-4 w-4 text-gray-400" />
                    <span className="font-mono text-sm">{box.sample_barcode || box.barcode}</span>
                    {count > 1 && <Badge value={`×${count}`} styles="bg-indigo-100 text-indigo-800" />}
                    {box.status && <Badge value={i18n.t(`stock_allocation.statuses.${box.status}`, { defaultValue: box.status })} styles={STATUS_BADGE_STYLES[box.status]} />}
                </div>
                <span className="text-xs text-gray-500">
                    {box.location?.code && <span className="font-mono mr-2">{box.location.code}</span>}
                    {piecesCount > 0 && <span>{piecesCount} {i18n.t('stock_allocation.pieces')}</span>}
                </span>
            </button>
            {expanded && <PieceList pieces={box.pieces} i18n={i18n} />}
        </div>
    )
}

const PalletBlock = ({ pallet, count, i18n }) => {
    const [expanded, setExpanded] = useState(false)
    const boxesCount = (pallet.boxes || []).length
    const Chevron = expanded ? ChevronDownIcon : ChevronRightIcon
    const groupedBoxes = useMemo(() => groupBoxes(pallet.boxes), [pallet.boxes])
    return (
        <div className="border border-gray-200 rounded p-3 bg-white">
            <button type="button" onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between text-left">
                <div className="flex items-center gap-2">
                    <Chevron className="h-4 w-4 text-gray-400" />
                    <span className="font-mono text-sm font-medium">{pallet.sample_barcode || pallet.barcode}</span>
                    {count > 1 && <Badge value={`×${count}`} styles="bg-indigo-100 text-indigo-800" />}
                    {pallet.status && <Badge value={i18n.t(`stock_allocation.statuses.${pallet.status}`, { defaultValue: pallet.status })} styles={STATUS_BADGE_STYLES[pallet.status]} />}
                </div>
                <span className="text-xs text-gray-500">
                    {pallet.location?.code && <span className="font-mono mr-2">{pallet.location.code}</span>}
                    {boxesCount > 0 && <span>{boxesCount} {i18n.t('stock_allocation.boxes')}</span>}
                </span>
            </button>
            {expanded && (
                <div className="flex flex-col gap-2 mt-3 pl-4 border-l-2 border-gray-100">
                    {groupedBoxes.map((b, idx) => <BoxBlock key={idx} box={b} count={b.count} i18n={i18n} />)}
                </div>
            )}
        </div>
    )
}

const ToteBlock = ({ tote, i18n }) => {
    const [expanded, setExpanded] = useState(false)
    const Chevron = expanded ? ChevronDownIcon : ChevronRightIcon
    return (
        <div className="border border-gray-200 rounded p-2 bg-white">
            <button type="button" onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between text-left">
                <div className="flex items-center gap-2">
                    <Chevron className="h-4 w-4 text-gray-400" />
                    <span className="font-mono text-sm">{tote.unique_code}</span>
                    {tote.status && <Badge value={i18n.t(`stock_allocation.statuses.${tote.status}`, { defaultValue: tote.status })} styles={STATUS_BADGE_STYLES[tote.status]} />}
                </div>
                <span className="text-xs text-gray-500">
                    {tote.boxes_count > 0 && <span className="mr-2">{tote.boxes_count} {i18n.t('stock_allocation.boxes')}</span>}
                    {tote.pieces_count > 0 && <span>{tote.pieces_count} {i18n.t('stock_allocation.pieces')}</span>}
                </span>
            </button>
            {expanded && Array.isArray(tote.contents) && tote.contents.length > 0 && (
                <ul className="mt-2 pl-4 text-xs text-gray-600 flex flex-col gap-1">
                    {tote.contents.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                            <span className="text-gray-400">{item.type === "box" ? i18n.t('stock_allocation.box') : i18n.t('stock_allocation.piece')}:</span>
                            <span className="font-mono">{item.barcode || item.sku || `#${item.stock_unit_id}`}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

const TasksBlock = ({ tasks, i18n }) => {
    if (!Array.isArray(tasks) || tasks.length === 0) return null
    return (
        <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-2">{i18n.t('stock_allocation.picking_progress')}</div>
            <div className="flex flex-col gap-1">
                {tasks.map((task) => {
                    const percent = task.expected_quantity ? Math.round((task.picked_quantity / task.expected_quantity) * 100) : 0
                    // The stock-allocation endpoint documents nested location/picker
                    // shapes; the picking-tasks summary endpoint uses flat ones.
                    // Read both to insulate from the inconsistency.
                    const locationCode = task.location_code || task.location?.code
                    const pickerName = task.picker?.name
                        || (task.picker ? `${task.picker.first_name || ""} ${task.picker.last_name || ""}`.trim() : "")
                        || (task.picker_id ? `Picker ${task.picker_id}` : "")
                    return (
                        <div key={task.id} className="flex items-center justify-between text-xs py-1 px-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                                <Badge value={i18n.t(`picking_tasks.pick_types.${task.pick_type}`, { defaultValue: task.pick_type })} styles={PICK_TYPE_STYLES[task.pick_type]} />
                                {locationCode && <span className="font-mono text-gray-600">{locationCode}</span>}
                                {pickerName && <span className="text-gray-500">— {pickerName}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-gray-600">{task.picked_quantity}/{task.expected_quantity}</span>
                                <span className="w-16 bg-gray-200 rounded-full h-1.5">
                                    <span className={`block h-1.5 rounded-full ${percent >= 100 ? "bg-green-500" : "bg-indigo-500"}`} style={{ width: `${Math.min(percent, 100)}%` }} />
                                </span>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export const StockAllocationSection = ({ orderId }) => {
    const { i18n } = useTranslation()
    const [expanded, setExpanded] = useState(false)

    const { data, isLoading, isError, error } = useQuery(
        ['order_stock_allocation', orderId],
        () => fetchOrderStockAllocation(orderId),
        { enabled: expanded && !!orderId, refetchOnWindowFocus: false }
    )

    const groupedPallets = useMemo(() => groupPallets(data?.pallets), [data])
    const groupedStandaloneBoxes = useMemo(() => groupBoxes(data?.standalone_boxes), [data])
    const groupedLoosePieces = useMemo(() => groupPieces(data?.loose_pieces), [data])

    const Chevron = expanded ? ChevronDownIcon : ChevronRightIcon
    const summary = data?.summary

    return (
        <div className="even:bg-gray-50 bg-white px-4 py-5 sm:px-6">
            <button type="button" onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between text-left">
                <div className="flex items-center gap-2">
                    <Chevron className="h-5 w-5 text-gray-400" />
                    <span className="text-base font-medium text-gray-500">{i18n.t('stock_allocation.section_title')}</span>
                    <BetaBadge />
                </div>
                {summary && (
                    <span className="text-xs text-gray-500">
                        {summary.pallets_count} {i18n.t('stock_allocation.pallets')} · {summary.boxes_count} {i18n.t('stock_allocation.boxes')} · {summary.loose_pieces_count} {i18n.t('stock_allocation.loose_pieces')} · {summary.totes_count} {i18n.t('stock_allocation.totes')}
                    </span>
                )}
            </button>
            {expanded && (
                <div className="mt-4">
                    {isLoading && <SkeletonLoader />}
                    {isError && (
                        <div className="text-sm text-red-600 py-2">
                            {error?.response?.data?.error || i18n.t('stock_allocation.error')}
                        </div>
                    )}
                    {data && (
                        <div className="flex flex-col gap-4">
                            {groupedPallets.length > 0 && (
                                <div>
                                    <div className="text-sm font-medium text-gray-700 mb-2">{i18n.t('stock_allocation.pallets')} ({summary?.pallets_count || 0})</div>
                                    <div className="flex flex-col gap-2">
                                        {groupedPallets.map((p, idx) => <PalletBlock key={idx} pallet={p} count={p.count} i18n={i18n} />)}
                                    </div>
                                </div>
                            )}

                            {groupedStandaloneBoxes.length > 0 && (
                                <div>
                                    <div className="text-sm font-medium text-gray-700 mb-2">{i18n.t('stock_allocation.standalone_boxes')}</div>
                                    <div className="flex flex-col gap-2">
                                        {groupedStandaloneBoxes.map((b, idx) => <BoxBlock key={idx} box={b} count={b.count} i18n={i18n} />)}
                                    </div>
                                </div>
                            )}

                            {groupedLoosePieces.length > 0 && (
                                <div>
                                    <div className="text-sm font-medium text-gray-700 mb-2">{i18n.t('stock_allocation.loose_pieces')}</div>
                                    <div className="flex flex-col gap-1">
                                        {groupedLoosePieces.map((p, idx) => <PieceRow key={idx} piece={p} i18n={i18n} />)}
                                    </div>
                                </div>
                            )}

                            {Array.isArray(data.totes) && data.totes.length > 0 && (
                                <div>
                                    <div className="text-sm font-medium text-gray-700 mb-2">{i18n.t('stock_allocation.totes')}</div>
                                    <div className="flex flex-col gap-2">
                                        {data.totes.map((t) => <ToteBlock key={t.tote_id} tote={t} i18n={i18n} />)}
                                    </div>
                                </div>
                            )}

                            {(groupedPallets.length === 0 && groupedStandaloneBoxes.length === 0 && groupedLoosePieces.length === 0 && (!data.totes || data.totes.length === 0)) && (
                                <div className="text-sm text-gray-500 py-2">{i18n.t('stock_allocation.empty')}</div>
                            )}

                            <TasksBlock tasks={data.tasks} i18n={i18n} />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
